import { FC, memo, useEffect, useRef, useState } from 'react';

import { Box } from '@mui/material';
// @ts-ignore
import * as pdfjsLib from 'pdfjs-dist/build/pdf';
// @ts-ignore
import pdfWorker from 'pdfjs-dist/build/pdf.worker.entry.js';

import ToastService from 'helpers/ToastService';

type PdfViewerProps = {
  pdfUrl: string;
  handlePdfLoaded?: () => void;
};

type PageItem = {
  page: any;
  viewport: any;
};

const PdfPageCanvas: FC<PageItem> = ({ page, viewport }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;

    if (!canvas) {
      return;
    }

    const context = canvas.getContext('2d');

    if (!context) {
      return;
    }

    const renderTask = page.render({
      canvasContext: context,
      viewport,
    });

    renderTask.promise.catch((error: Error & { name?: string }) => {
      if (error.name !== 'RenderingCancelledException') {
        ToastService.showError(error.message);
      }
    });

    return () => {
      renderTask?.cancel();
    };
  }, [page, viewport]);

  return (
    <canvas
      style={{
        marginBottom: '10px',
        width: `100%`,
        height: `100%`,
      }}
      width={viewport.width}
      height={viewport.height}
      ref={canvasRef}
    />
  );
};

export const PdfViewer: FC<PdfViewerProps> = memo(({ pdfUrl, handlePdfLoaded }) => {
  const [pages, setPages] = useState<PageItem[]>([]);

  useEffect(() => {
    pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;
    setPages([]);
    let isDisposed = false;

    const fetchPdf = async () => {
      try {
        const pdf = await pdfjsLib.getDocument(pdfUrl).promise;
        const content: PageItem[] = [];

        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
          const page = await pdf.getPage(pageNum);

          const scale = 2.5;
          const viewport = page.getViewport({ scale: scale });

          content.push({
            page,
            viewport,
          });
        }
        if (!isDisposed) {
          setPages(content);
          handlePdfLoaded?.();
        }
      } catch (error) {
        if (!isDisposed && error instanceof Error) {
          ToastService.showError(error.message);
        }
      }
    };

    fetchPdf();

    return () => {
      isDisposed = true;
    };
  }, [pdfUrl]);

  return (
    <Box display="flex" flexDirection="column" alignItems="center">
      {pages.map(({ page, viewport }, index) => (
        <PdfPageCanvas key={`${index}-${viewport.width}-${viewport.height}`} page={page} viewport={viewport} />
      ))}
    </Box>
  );
});
